import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PrintService } from '../../services/print.service';

interface SalesLedgerControlRow {
	type: number;
	typename: string;
	typeno: number;
	order_?: number;
	debtorno?: string;
	customername?: string;
	periodno: number;
	periodname?: string;
	trandate?: string;
	linecount?: number;
	difference: number;
	gl_url: string;
}

interface GlJournalLine {
	accountcode: string;
	accountname: string;
	narrative: string;
	amount: number;
	debit: number;
	credit: number;
	periodno: number;
	periodname: string;
	tag: string;
	tagdescription: string;
	chequeno: string;
	trandate: string;
}

interface GlJournalDetailModalData {
	header: {
		type: number;
		typename: string;
		typeno: number;
		periodno: number;
		periodname: string;
		trandate: string;
	};
	lines: GlJournalLine[];
	difference: number;
}

@Component({
	selector: 'app-sales-ledger-control-check',
	templateUrl: './sales-ledger-control-check.component.html',
	styleUrls: ['./sales-ledger-control-check.component.scss']
})
export class SalesLedgerControlCheckComponent implements OnInit {
	@ViewChild('print_table') printTableRef: ElementRef;
	@ViewChild('journalDetails', { static: false }) journalDetailsRef: TemplateRef<any>;
	color: any = '';
	title = 'Unbalanced Transactions';
	loading = false;
	exporting = false;
	rows: SalesLedgerControlRow[] = [];
	displayedColumns: string[] = ['type', 'typeno', 'order_', 'customername', 'periodno', 'trandate', 'linecount', 'difference', 'actions'];
	totalDifference = 0;
	totalAbsDifference = 0;
	selectedJournal: GlJournalDetailModalData | null = null;
	modalTotalDebit = 0;
	modalTotalCredit = 0;

	constructor(
		private location: Location,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private modalService: NgbModal,
		private printService: PrintService
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		this.runCheck();
	}

	back(): void {
		this.location.back();
	}

	runCheck(): void {
		this.loading = true;
		this.rows = [];

		this.generalLedgerService.getSalesLedgerControlCheck({}).subscribe(
			(results: any) => {
				this.loading = false;
				if (results && Array.isArray(results.rows)) {
					// Normalize rows to the expected shape
					this.rows = results.rows.map((row: any) => ({
						type: Number(row.type),
						typename: row.typename,
						typeno: Number(row.typeno),
						order_: row.order_ !== undefined && row.order_ !== null ? Number(row.order_) : undefined,
						debtorno: row.debtorno,
						customername: row.customername,
						periodno: Number(row.periodno),
						periodname: row.periodname,
						trandate: row.trandate,
						linecount: row.linecount !== undefined ? Number(row.linecount) : undefined,
						difference: Number(row.difference),
						gl_url: row.gl_url
					}));

					this.totalDifference = this.rows.reduce((sum, r) => sum + r.difference, 0);
					this.totalAbsDifference = this.rows.reduce((sum, r) => sum + Math.abs(r.difference), 0);

					if (results.title) {
						this.title = results.title;
					}
				}
			},
			() => {
				// On error, just stop loading; error handling is performed centrally in the service
				this.loading = false;
			}
		);
	}

	viewGl(row: SalesLedgerControlRow): void {
		this.generalLedgerService.getGlTransDetails({ type: row.type, typeno: row.typeno }).subscribe((result: any) => {
			if (!result || !result.success) { return; }

			this.selectedJournal = {
				header: result.header,
				lines: result.lines,
				difference: result.difference
			};

			this.modalTotalDebit = this.selectedJournal.lines.reduce((sum, line) => sum + (line.debit || 0), 0);
			this.modalTotalCredit = this.selectedJournal.lines.reduce((sum, line) => sum + (line.credit || 0), 0);

			this.modalService.open(this.journalDetailsRef, {
				ariaLabelledBy: 'modal-title',
				size: 'xl',
				animation: false
			}).result.finally(() => {
				this.selectedJournal = null;
			});
		});
	}

	exportPdf(): void {
		if (!this.rows || !this.rows.length || !this.printTableRef) {
			return;
		}

		this.exporting = true;
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);

		const data = {
			content: encoded,
			filename: `unbalanced_transactions_${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`,
			title: this.title,
			landscape: true
		};

		this.printService.pdf(data).subscribe(
			(result: any) => {
				this.exporting = false;
				if (result && result.content) {
					this.globalSearchService.downloadPdf(result.content, data.filename);
				}
			},
			() => {
				this.exporting = false;
			}
		);
	}

	exportXls(): void {
		if (!this.rows || !this.rows.length || !this.printTableRef) {
			return;
		}

		this.exporting = true;
		const now = new Date();
		const encoded: string = this.globalSearchService.base64encode(this.printTableRef.nativeElement.innerHTML);

		const data = {
			content: encoded,
			filename: `unbalanced_transactions_${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`,
			title: this.title,
			subtitle: `Count: ${this.rows.length}`
		};

		this.printService.xls(data).subscribe(
			(result: any) => {
				this.exporting = false;
				if (result && result.content) {
					this.globalSearchService.downloadXls(result.content, data.filename);
				}
			},
			() => {
				this.exporting = false;
			}
		);
	}
}


